import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SessionService } from '../common/session/session.service';
import { EventType } from '../../../environments/common';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from '../utils/utils.service';
import { SearchService } from 'app/components/search/search.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  googleTagManagerEvents: any ={
    VIEW_ITEM_LIST: 'view_item_list',
    SELECT_ITEM: 'select_item',
    VIEW_ITEM: 'view_item',
    ADD_TO_CART: 'add_to_cart',             // add to favorites
    REMOVE_FROM_CART: 'remove_from_cart',   // remove from favorites
    VIEW_CART: 'view_cart',                 // view favorites
    LOGIN: 'login',
    FILTERS: 'filters',
    SHARE: 'share',
    CONTACT: 'contact',
    SEARCH: 'search'
  };

  notSet: string = '';

  tab: string;
  filter: string;

  eventData: EventType = JSON.parse(this.sessionService.get('eventData', false) || '{}');

  sendSelectItemIndex: number = 0;
  sendViewItemListIndex: number = 0;

  constructor(private sessionService: SessionService, private route: ActivatedRoute, public translateService: TranslateService, private utilsService: UtilsService,
                private searchService: SearchService) {}

  insertGtmScripts(gtmId: string): void {
    console.log("insertGtmScripts", gtmId);
    if (!document.getElementById("gtmEvent")) {
      this.insertGtmInHead(gtmId);
    }
    if (!document.getElementById("gtmIframe")) {
      this.insertGtmInBody(gtmId);
    }
  }

  insertGtmInHead(gtmId: string): void {
    const script = document.createElement('script');
    script.setAttribute('id', 'gtmEvent');
    script.type = 'text/javascript';
    script.async = true;
    script.text = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.insertBefore(script, document.head.childNodes[0]);
  }

   insertGtmInBody(gtmId: string): void {
     const noScript = document.createElement('noscript');
     noScript.setAttribute('id', 'gtmIframe');
     const iframe = document.createElement('iframe');
     const url = 'https://www.googletagmanager.com/ns.html?id=' + gtmId;
     iframe.setAttribute('src', url);
     iframe.height = '0';
     iframe.width = '0';
     iframe.style.display = 'none';
     iframe.style.visibility = 'hidden';
     noScript.appendChild(iframe);
     document.body.insertBefore(noScript, document.body.childNodes[0]);
   }

   initAnalytics(gtmId:string): void {
     /** Adds here code to init analytics */
     this.sessionService.set(`analyticsLoaded_${this.eventData.sapCode}`, false);
     const intervalId = setInterval( () => {
       if (window['dataLayer']) {
         window['dataLayer'] = window['dataLayer'] || [];
         this.gtag('js', new Date());
         this.gtag('config', gtmId);
         /** Stop interval and launch first send page view */
         this.sessionService.set(`analyticsLoaded_${this.eventData.sapCode}`, true);
         clearInterval(intervalId);
         this.sendPageView(window.location.href);
       }
     }, 50);
   }

   gtag(...args: any[]){window['dataLayer'].push(arguments)}

   analyticsLoaded(): boolean {
      window['dataLayer'] = window['dataLayer'] || [];
      return (JSON.parse(this.sessionService.get(`analyticsLoaded_${this.eventData.sapCode}`)) == true);
   }

   getUserId(): string {
     const userProfile = JSON.parse(this.sessionService.get('profile_' + this.eventData.sapCode, true));
     return userProfile ? userProfile.userId : this.notSet;
   }

   getTranslationForCatalogueLanguage(translations: Array<any>): string {
     const language = this.utilsService.mapLanguageCode(this.translateService.getDefaultLang());
     return translations.find(t => t.language === language).name ? translations.find(t => t.language === language).name : translations.find(t => t.language === language).translation;
   }

   getStandLocationBeatified(stand: string) {
     try {
       var standLocation = this.utilsService.standConvertor(stand);
     } catch(e) {
       standLocation = { successful: false };
     }
     var standInfo = stand;
     if (standLocation.successful) {
        const place = this.translateService.instant('STAND_DISPLAY.' + standLocation.place);
        const pavilion = this.translateService.instant('STAND_DISPLAY.PAVILION');
        const pavilionName = this.translateService.instant(this.utilsService.pavilionTranslationFinder(standLocation.pavilion, standLocation.place));
        const level = this.translateService.instant('STAND_DISPLAY.LEVEL');
        const street = this.translateService.instant('STAND_DISPLAY.STREET');
        const standNumber = this.translateService.instant('STAND_DISPLAY.STAND');
        standInfo = `${place}, ${pavilion} ${pavilionName}, ${level} ${standLocation.level}, ${street} ${standLocation.street}, ${standNumber} ${standLocation.stand}`;
     }
     return standLocation.successful ? standInfo : stand;
   }

   getLabelFromPropertyName(propertyName: string): string {
     var label = propertyName;
     switch(propertyName) {
       case "selectedHierarchicalProperties": label = 'hierarchical'; break;
       case "selectedProperties": label = 'master'; break;
       case "selectedCountries": label = 'countries'; break;
       case "selectedSectors": label = 'sectors'; break;
       case "selectedHomeOds": label = 'ods'; break;
       case "selectedMultiEvents": label = 'multi_events'; break;
     }
     return label;
   }

   getAppliedFiltersNames(): Array<string> {

      const appliedFilters: Array<string> = [];

      const selectedFilters = this.searchService.getFilterAnalytics()
      const searchConfig = this.searchService.getSearchConfig();

      for (let i = 0; i < selectedFilters.length; i++) {
        if (selectedFilters[i].translations) {
            appliedFilters.push(this.getTranslationForCatalogueLanguage(selectedFilters[i].translations));
        } else {
            appliedFilters.push(selectedFilters[i].name);
        }
      }

      switch(searchConfig.searchOrder) {
        case "BY_RELEVANCE": appliedFilters.push('relevance'); break;
        case "A_Z": appliedFilters.push('from_a_to_z'); break;
        case "Z_A": appliedFilters.push('from_z_to_a'); break;
      }
      return appliedFilters;
   }

   transformDataToItems(exhibitorsList: Array<any>, productsList: Array<any>, format?: string, itemListId?: number): Array<any> | string {

     var items: Array<any> = [];
     var index = 0;

     exhibitorsList.forEach((exhibitor) => {
       if(exhibitor.type !== 'banner') {
          var newItem: any = {
            item_name: exhibitor.exhibitorName ? exhibitor.exhibitorName : exhibitor.name,
            item_id: exhibitor.idExhibitor ? exhibitor.idExhibitor : exhibitor.entityId ? exhibitor.entityId : exhibitor.id,
            affiliation: exhibitor.exhibitorName ? exhibitor.exhibitorName : exhibitor.name,
            affiliation_id: exhibitor.idExhibitor ? exhibitor.idExhibitor : exhibitor.entityId ? exhibitor.entityId : exhibitor.id,
            currency: this.notSet,
            index: index,
            item_brand: exhibitor.eventName ? exhibitor.eventName : this.eventData.eventName,
            item_variant: 'Exhibitor',
            price: this.notSet,
            quantity: this.notSet,
            location_id: exhibitor.standLocation ? this.getStandLocationBeatified(exhibitor.standLocation) : this.notSet,
          }

          if (exhibitor.categories && exhibitor.categories.length > 0) {
            let categoryIndex = 1;
            for (let category of exhibitor.categories) {
              let propertyName = 'item_category';
              if (categoryIndex != 1 ) propertyName += categoryIndex;
              newItem[propertyName] = this.getTranslationForCatalogueLanguage(category.translations);
              categoryIndex++;
            }
          } else {
            newItem['item_category'] = this.notSet;
          }

          if(format) {
            newItem['item_list_id'] = itemListId;
            newItem['item_list_name'] = format;
          }

          index++;
          items.push(newItem);
       }

     });

     productsList.forEach((product) => {
       if(product.type !== 'banner') {
         var newItem: any = {
           item_name: product.productTranslation ? this.getTranslationForCatalogueLanguage(product.productTranslation) : this.getTranslationForCatalogueLanguage(product.translations),
           item_id: product.idProduct ? product.idProduct : product.entityId ? product.entityId : product.id,
           affiliation: product.exhibitorName,
           affiliation_id: product.exhibitorId ? product.exhibitorId : product.idExhibitor,
           currency: this.notSet,
           index: index,
           item_brand: product.eventName ? product.eventName : this.eventData.eventName,
           item_variant: 'Product',
           price: this.notSet,
           quantity: this.notSet,
           location_id: product.standLocation ? this.getStandLocationBeatified(product.standLocation) : this.notSet,
         }

         if (product.categories && product.categories.length > 0) {
           let categoryIndex = 1;
           for (let category of product.categories) {
             let propertyName = 'item_category';
             if (categoryIndex != 1 ) propertyName += categoryIndex;
             newItem[propertyName] = this.getTranslationForCatalogueLanguage(category.translations);
             categoryIndex++;
           }
         } else {
           newItem['item_category'] = this.notSet;
         }

         if(format) {
           newItem['item_list_id'] = itemListId;
           newItem['item_list_name'] = format;
         }

         index++;
         items.push(newItem);
       }

     });

     if (items.length > 0) {
      return items;
     } else {
      return this.notSet;
     }
   }

   sendPageView(pageView:string): void {
     if(this.analyticsLoaded()) {
       window['dataLayer'].push({
         'event': 'virtual_page_view',
         'brand': this.eventData.eventName,
         'page_title': pageView,
         'page_location': window.location.href,
         'event_code': this.eventData.sapCode
       });
     }
   }

   sendViewItemList(listItems: Array<any>, type: string, format: string): void {
     if(this.analyticsLoaded()) {
       var items: Array<any> | string = this.notSet;
       if (type === 'product') {
         items = this.transformDataToItems([], listItems, type + '_' + format, this.sendViewItemListIndex);
       } else if (type === 'exhibitor') {
         items = this.transformDataToItems(listItems, [], type + '_' + format, this.sendViewItemListIndex);
       }

       if(this.utilsService.isEmptyOrUndefined(window['dataLayer']))//FIXME window['dataLayer'] undefined
        window['dataLayer'] = window['dataLayer'] || [];

        window['dataLayer'].push({ ecommerce: null }); // Clear the previous ecommerce object.
        window['dataLayer'].push({
          event: this.googleTagManagerEvents.VIEW_ITEM_LIST,
          user_id: this.getUserId(),
          ecommerce: {
            item_list_id: this.sendViewItemListIndex,
            item_list_name: type + '_' + format,
            items: items
          }
        });
        this.sendViewItemListIndex++;
     }
   }

    sendViewItem(item: any, type: string, categories: Array<any>): void {
      if(this.analyticsLoaded()) {
        var items: Array<any> | string = this.notSet;
        item['categories'] = categories;
        if (type === 'product') {
          items = this.transformDataToItems([], [item]);
        } else if (type === 'exhibitor') {
          items = this.transformDataToItems([item], []);
        }

        if(this.utilsService.isEmptyOrUndefined(window['dataLayer']))//FIXME window['dataLayer'] undefined
          window['dataLayer'] = window['dataLayer'] || [];

        window['dataLayer'].push({ ecommerce: null }); // Clear the previous ecommerce object.
        window['dataLayer'].push({
          event: this.googleTagManagerEvents.VIEW_ITEM,
          user_id: this.getUserId(),
          ecommerce: {
            value: this.notSet,
            currency: this.notSet,
            items: items
          }
        });
      }
    }

    sendSelectItem(item: any, type: string, format: string): void {
      if(this.analyticsLoaded()) {
        var items: Array<any> | string = this.notSet;
        if (type === 'product') {
          items = this.transformDataToItems([], [item], type + '_' + format, this.sendSelectItemIndex);
        } else if (type === 'exhibitor') {
          items = this.transformDataToItems([item], [], type + '_' + format, this.sendSelectItemIndex);
        }
        window['dataLayer'].push({ ecommerce: null }); // Clear the previous ecommerce object.
        window['dataLayer'].push({
          event: this.googleTagManagerEvents.SELECT_ITEM,
          user_id: this.getUserId(),
          ecommerce: {
            item_list_id: this.sendSelectItemIndex,
            item_list_name: type + '_' + format,
            items: items
          }
        });
        this.sendSelectItemIndex++;
      }
    }

    sendAddToFavorites(item: any, type: string): void {
      if(this.analyticsLoaded()) {
        var items: Array<any> | string = this.notSet;
        if (type === 'product') {
          items = this.transformDataToItems([], [item]);
        } else if (type === 'exhibitor') {
          items = this.transformDataToItems([item], []);
        }
        window['dataLayer'].push({ ecommerce: null }); // Clear the previous ecommerce object.
        window['dataLayer'].push({
          event: this.googleTagManagerEvents.ADD_TO_CART,
          user_id: this.getUserId(),
          ecommerce: {
            value: this.notSet,
            currency: this.notSet,
            items: items
          }
        });
      }
    }

    sendRemoveFromFavorites(item: any, type: string): void {
      if(this.analyticsLoaded()) {
        var items: Array<any> | string = this.notSet;
        if (type === 'product') {
          items = this.transformDataToItems([], [item]);
        } else if (type === 'exhibitor') {
          items = this.transformDataToItems([item], []);
        }
        window['dataLayer'].push({ ecommerce: null }); // Clear the previous ecommerce object.
        window['dataLayer'].push({
          event: this.googleTagManagerEvents.REMOVE_FROM_CART,
          user_id: this.getUserId(),
          ecommerce: {
            value: this.notSet,
            currency: this.notSet,
            items: items
          }
        });
      }
    }

    sendViewFavorites(exhibitorsList: Array<any>, productsList: Array<any>): void {
      if(this.analyticsLoaded()) {
        this.route.queryParams.subscribe(params => {
          this.tab = params['tab'];
          this.filter = params['filter'];
        });

        var items: Array<any> | string = this.notSet;
        if ( (this.tab === 'favorites' && !this.filter) ) {
          items = this.transformDataToItems(exhibitorsList, productsList);
        } else if (this.tab === 'favorites' && this.filter === 'ONLY_PRODUCTS') {
          items = this.transformDataToItems([], productsList);
        } else if (this.tab === 'favorites' && this.filter === 'ONLY_EXHIBITORS') {
          items = this.transformDataToItems(exhibitorsList, []);
        }

        window['dataLayer'].push({ ecommerce: null }); // Clear the previous ecommerce object.
        window['dataLayer'].push({
          event: this.googleTagManagerEvents.VIEW_CART,
          user_id: this.getUserId(),
          ecommerce: {
            value: this.notSet,
            currency: this.notSet,
            items: items
          }
        });
      }
    }

    sendShare(contentType: string, itemId: string, method: string): void {
       if(this.analyticsLoaded()) {
         window['dataLayer'].push({ ecommerce: null });
         window['dataLayer'].push({
           event: this.googleTagManagerEvents.SHARE,
           user_id: this.getUserId(),
           content_type: contentType,
           item_id: itemId,
           method: method
         });
       }
    }

   sendContact(countryId: number, zipCode: string): void {
      const countriesMap = JSON.parse(this.sessionService.get('countriesMap'));
      if(this.analyticsLoaded()) {
        window['dataLayer'].push({ ecommerce: null });
        window['dataLayer'].push({
          event: this.googleTagManagerEvents.CONTACT,
          user_id: this.getUserId(),
          demo_country: countriesMap ? countriesMap[countryId].name : countryId,
          demo_zip_code: zipCode
        });
      }
   }

   sendLogin(status: string, error: string): void {
      if(this.analyticsLoaded()) {
        window['dataLayer'].push({ ecommerce: null });
        window['dataLayer'].push({
          event: this.googleTagManagerEvents.LOGIN,
          user_id: this.getUserId(),
          status: status,
          descError: error
        });
      }
   }

   sendSearch(searchText: number): void {
     if(this.analyticsLoaded()) {
       window['dataLayer'].push({ ecommerce: null });
       window['dataLayer'].push({
         event: this.googleTagManagerEvents.SEARCH,
         user_id: this.getUserId(),
         text: searchText,
         origin: window.location.href
       });
     }
   }

   sendFilters(filterAction: string, propertyName: string, translations: Array<any>, filterId: number): void {
     const filterParameter = this.getLabelFromPropertyName(propertyName);
     const filterValue = (propertyName == 'order' || propertyName == 'selectedMultiEvents') ? translations : this.getTranslationForCatalogueLanguage(translations);
     const appliedFilters = this.getAppliedFiltersNames();
     if(this.analyticsLoaded()) {
       window['dataLayer'].push({ ecommerce: null });
       window['dataLayer'].push({
         event: this.googleTagManagerEvents.FILTERS,
         user_id: this.getUserId(),
         ecommerce: {
           filter_parameter: filterParameter,
           filter_value: filterValue,
           filter_id: filterId,
           filter_action: filterAction,
           filters_selected: appliedFilters
         }
       });
     }
   }

}
